import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LoadError, errMessage } from "@/components/LoadError";
import { SectieKop, LIJST, KAART } from "@/components/rust/Rust";
import { AntwoordVeld, AntwoordLezen } from "@/components/MijnPuntAntwoord";
import { cap, datumKort, effDeadline } from "@/components/afspraken/afsprakenUtils";
import { NieuweAfdelingAfspraak } from "@/components/afdelingen/NieuweAfdelingAfspraak";

const segBtn = (on) => `px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${on ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`;

function Werkbon({ a, onSave }) {
  const [w, setW] = useState(a.werkbon || "");
  useEffect(() => { setW(a.werkbon || ""); }, [a.werkbon]);
  const bewaar = () => { if (w.trim() !== (a.werkbon || "")) onSave(w); };
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Werkbon</span>
      <input data-testid={`afd-werkbon-${a.id}`} maxLength={40} value={w} onChange={(e) => setW(e.target.value)} onBlur={bewaar} onKeyDown={(e) => e.key === "Enter" && bewaar()}
        placeholder="bijv. TD-2214" className="mt-1 w-full px-2.5 py-2 rounded-md border border-zinc-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
    </label>
  );
}

// Eén afspraak: werkbon + antwoord bij de deadline en elk peilmoment (A4). Afgerond = alleen lezen.
function AfspraakKaart({ a, onMeld }) {
  const open = a.status === "open";
  const rep = (id) => (a.afdelingReported || {})[id] || {};
  const punten = [...(a.checkpoints || []).map((c) => ({ id: c.id, label: `Peilmoment ${datumKort(c.date)}${c.wat ? ` · ${c.wat}` : ""}` })),
    { id: "deadline", label: a.deadline ? `Deadline ${datumKort(a.deadline)} — wat is de stand?` : "Stand van zaken" }];   // R5: altijd één moment
  return (
    <li data-testid={`afd-afspraak-${a.id}`} className="px-3 py-3 space-y-2.5">
      <div>
        <p className="text-[14px] font-semibold text-zinc-900">{a.wat}</p>
        <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px] font-semibold">
          <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700">{a.locatie || "Algemeen"}</span>
          {a.overdue && <span className="px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700">te laat</span>}
          <span className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700">door {cap(a.door)}</span>
          {!open && <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600">{a.status === "nagekomen" ? "Nagekomen" : "Niet nagekomen"}</span>}
          {effDeadline(a) && open && <span className="px-2 py-0.5 rounded-full bg-white border border-zinc-200 text-zinc-600 font-mono">{datumKort(effDeadline(a))}</span>}
        </div>
      </div>
      {open ? <Werkbon a={a} onSave={(w) => onMeld(a, { id: "deadline", werkbon: w })} />
        : a.werkbon && <p className="font-mono text-xs text-zinc-600">Werkbon {a.werkbon}</p>}
      {punten.map((p) => (
        <div key={p.id} data-testid={`afd-punt-${a.id}-${p.id}`}>
          <p className="text-[12px] font-semibold text-zinc-600">{p.label}</p>
          {open ? <AntwoordVeld rep={rep(p.id)} ids={{ note: `afd-note-${a.id}-${p.id}`, gereed: `afd-gereed-${a.id}-${p.id}`, opslaan: `afd-opslaan-${a.id}-${p.id}` }}
              onSave={(note) => onMeld(a, { id: p.id, note })} onToggle={() => onMeld(a, { id: p.id, reported: !rep(p.id).reported })} />
            : null}
          {open ? <p className="text-[11px] text-zinc-500">De OM's en oversight zien dit. De locatie niet.</p>
            : <AntwoordLezen rep={rep(p.id)} testid={`afd-lezen-${a.id}-${p.id}`} />}
        </div>
      ))}
    </li>
  );
}

// v19-C3: Afspraken van de afdeling: Open/Afgerond, alleen lezen behalve melding en werkbon.
export default function AfdelingAfspraken() {
  const [lijst, setLijst] = useState(null);
  const [fout, setFout] = useState("");
  const [tab, setTab] = useState("open");
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [metaFout, setMetaFout] = useState("");
  const laadMeta = () => { setMetaFout(""); api.get("/meta").then((r) => setMeta(r.data)).catch((e) => setMetaFout(errMessage(e))); };
  useEffect(laadMeta, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const laad = useCallback(() => api.get("/afspraken").then((r) => { setLijst(r.data); setFout(""); }).catch((e) => setFout(errMessage(e))), []);
  useEffect(() => { laad(); }, [laad]);
  const sel = params.get("select");
  useEffect(() => {
    if (!lijst || !sel) return;
    const a = lijst.find((x) => x.id === sel);
    if (a && a.status !== "open") setTab("afgerond");
    setTimeout(() => document.querySelector(`[data-testid="afd-afspraak-${sel}"]`)?.scrollIntoView({ block: "center" }), 150);
  }, [lijst, sel]);
  const meld = async (a, body) => {
    try {
      const r = await api.patch(`/afspraken/${a.id}/afdeling-melding`, body);
      setLijst((l) => l.map((x) => (x.id === a.id ? r.data : x)));
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt. Je tekst staat nog in het vak."); return false; }
  };
  if (fout) return <LoadError error={fout} onRetry={laad} testid="afd-afspraken-error" />;
  if (!lijst) return <div data-testid="afd-afspraken-laden" className="p-12 text-center text-sm text-zinc-400">Laden…</div>;
  const open = lijst.filter((a) => a.status === "open").sort((x, y) => (y.overdue - x.overdue) || (effDeadline(x) || "9").localeCompare(effDeadline(y) || "9"));
  const dicht = lijst.filter((a) => a.status !== "open").sort((x, y) => (y.afgerondOp || "").localeCompare(x.afgerondOp || ""));
  const items = tab === "open" ? open : dicht;
  return (
    <div data-testid="afd-afspraken-page" className="pb-24 md:pb-0 max-w-3xl">
      <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Afspraken</h1>
      <p className="text-sm text-zinc-500 mb-3">De afspraken van je afdeling. Meld bij de deadline en de peilmomenten hoe het staat. Een nieuwe afspraak voor je afdeling maak je met Nieuwe afspraak.</p>
      {metaFout ? (
        <div data-testid="afd-nieuw-meta-fout" className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">
          <span>Nieuwe afspraak kan nu niet: de gegevens laadden niet. {metaFout}</span>
          <button type="button" data-testid="afd-nieuw-meta-opnieuw" onClick={laadMeta} className="min-h-[44px] sm:min-h-0 font-semibold underline">Opnieuw proberen</button>
        </div>
      ) : meta && <NieuweAfdelingAfspraak meta={meta} onToegevoegd={(a) => { setTab("open"); setLijst((l) => [a, ...l]); }} />}
      <div className="flex gap-1 mb-3 rounded-md border border-zinc-300 bg-white p-0.5 w-fit">
        <button type="button" data-testid="afd-tab-open" onClick={() => setTab("open")} className={segBtn(tab === "open")}>Open · {open.length}</button>
        <button type="button" data-testid="afd-tab-afgerond" onClick={() => setTab("afgerond")} className={segBtn(tab === "afgerond")}>Afgerond · {dicht.length}</button>
      </div>
      <SectieKop titel={tab === "open" ? "Open" : "Afgerond"} aantal={items.length} />
      {items.length ? <ul className={LIJST} data-testid="afd-afspraken-lijst">{items.map((a) => <AfspraakKaart key={a.id} a={a} onMeld={meld} />)}</ul>
        : <div className={`${KAART} px-3 py-4 text-sm text-zinc-400`} data-testid="afd-afspraken-leeg">{tab === "open" ? "Geen open afspraken." : "Nog niets afgerond."}</div>}
      <div className={`${KAART} mt-4 px-3 py-3 bg-zinc-50`}>
        <p className="text-[12px] font-semibold text-zinc-700">Wat je niet ziet</p>
        <p className="text-xs text-zinc-500">Calls, cijfers en bezoeken van de locaties blijven bij de OM's en oversight. Jij ziet alleen de afspraken van je eigen afdeling.</p>
      </div>
    </div>
  );
}
